import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import api from '../services/api';

// Types
interface ReportsState {
  dailyStats: any;
  weeklyStats: any;
  monthlyStats: any;
  realtimeStats: any;
  advancedRealtimeStats: any;
  activeAgents: any[];
  waitingCalls: any[];
  queuePerformance: any[];
  hourlyDistribution: any[];
  agentPerformance: any;
  allAgentsPerformance: any[];
  queuePerformanceDetails: any;
  dailyBreakdown: any[];
  weeklyBreakdown: any[];
  periodComparison: any;
  loading: boolean;
  error: string | null;
}

interface ReportsAction {
  type: string;
  payload?: any;
}

interface ReportsContextType {
  dailyStats: any;
  weeklyStats: any;
  monthlyStats: any;
  realtimeStats: any;
  advancedRealtimeStats: any;
  activeAgents: any[];
  waitingCalls: any[];
  queuePerformance: any[];
  hourlyDistribution: any[];
  agentPerformance: any;
  allAgentsPerformance: any[];
  queuePerformanceDetails: any;
  dailyBreakdown: any[];
  weeklyBreakdown: any[];
  periodComparison: any;
  loading: boolean;
  error: string | null;
  fetchAllReports: () => Promise<void>;
  refreshReports: () => Promise<void>;
  fetchDailyStats: (date?: string) => Promise<any>;
  fetchWeeklyStats: (startDate: string, locationId?: string) => Promise<any>;
  fetchMonthlyStats: (year: number, month: number, locationId?: string) => Promise<any>;
  fetchRealtimeStats: () => Promise<any>;
  fetchAdvancedRealtimeStats: (locationId?: string) => Promise<any>;
  fetchActiveAgents: (locationId?: string) => Promise<any[]>;
  fetchWaitingCalls: (locationId?: string) => Promise<any[]>;
  fetchQueuePerformance: (locationId?: string) => Promise<any[]>;
  fetchHourlyDistribution: (date?: string) => Promise<any[]>;
  fetchAgentPerformance: (agentId: string, startDate: string, endDate: string) => Promise<any>;
  fetchAllAgentsPerformance: (startDate: string, endDate: string, locationId?: string) => Promise<any[]>;
  fetchQueuePerformanceDetails: (queueId: string, startDate: string, endDate: string) => Promise<any>;
  fetchDailyBreakdown: (startDate: string, endDate: string, locationId?: string) => Promise<any[]>;
  fetchWeeklyBreakdown: (startDate: string, endDate: string, locationId?: string) => Promise<any[]>;
  fetchPeriodComparison: (period1Start: string, period1End: string, period2Start: string, period2End: string, locationId?: string) => Promise<any>;
  exportToCSV: (data: any, filename: string) => Promise<any>;
  exportToPDF: (data: any, filename: string) => Promise<any>;
  clearError: () => void;
}

// Reports Context
const ReportsContext = createContext<ReportsContextType | undefined>(undefined);

// Reports Reducer
const reportsReducer = (state: ReportsState, action: ReportsAction): ReportsState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_DAILY_STATS':
      return { ...state, dailyStats: action.payload, loading: false };
    case 'SET_WEEKLY_STATS':
      return { ...state, weeklyStats: action.payload, loading: false };
    case 'SET_MONTHLY_STATS':
      return { ...state, monthlyStats: action.payload, loading: false };
    case 'SET_REALTIME_STATS':
      return { ...state, realtimeStats: action.payload, loading: false };
    case 'SET_ADVANCED_REALTIME_STATS':
      return { ...state, advancedRealtimeStats: action.payload, loading: false };
    case 'SET_ACTIVE_AGENTS':
      return { ...state, activeAgents: action.payload, loading: false };
    case 'SET_WAITING_CALLS':
      return { ...state, waitingCalls: action.payload, loading: false };
    case 'SET_QUEUE_PERFORMANCE':
      return { ...state, queuePerformance: action.payload, loading: false };
    case 'SET_HOURLY_DISTRIBUTION':
      return { ...state, hourlyDistribution: action.payload, loading: false };
    case 'SET_AGENT_PERFORMANCE':
      return { ...state, agentPerformance: action.payload, loading: false };
    case 'SET_ALL_AGENTS_PERFORMANCE':
      return { ...state, allAgentsPerformance: action.payload, loading: false };
    case 'SET_QUEUE_PERFORMANCE_DETAILS':
      return { ...state, queuePerformanceDetails: action.payload, loading: false };
    case 'SET_DAILY_BREAKDOWN':
      return { ...state, dailyBreakdown: action.payload, loading: false };
    case 'SET_WEEKLY_BREAKDOWN':
      return { ...state, weeklyBreakdown: action.payload, loading: false };
    case 'SET_PERIOD_COMPARISON':
      return { ...state, periodComparison: action.payload, loading: false };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
};

// Initial State
const initialState = {
  dailyStats: null,
  weeklyStats: null,
  monthlyStats: null,
  realtimeStats: null,
  advancedRealtimeStats: null,
  activeAgents: [],
  waitingCalls: [],
  queuePerformance: [],
  hourlyDistribution: [],
  agentPerformance: null,
  allAgentsPerformance: [],
  queuePerformanceDetails: null,
  dailyBreakdown: [],
  weeklyBreakdown: [],
  periodComparison: null,
  loading: false,
  error: null
};

// Reports Provider
interface ReportsProviderProps {
  children: ReactNode;
}

export const ReportsProvider = ({ children }: ReportsProviderProps) => {
  const [state, dispatch] = useReducer(reportsReducer, initialState);


  // Set loading
  const setLoading = (loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: loading });
  };

  // Set error
  const setError = (error: string) => {
    dispatch({ type: 'SET_ERROR', payload: error });
  };

  // Clear error
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // Fetch daily statistics
  const fetchDailyStats = async (date = new Date().toISOString().split('T')[0]) => {
    try {
      setLoading(true);
      clearError();

      const response = await api.get(`/reports/daily?date=${date}`);

      if (response.data.success) {
        dispatch({ type: 'SET_DAILY_STATS', payload: response.data.data });
        return response.data.data;
      } else {
        setError(response.data.message || 'Günlük istatistikler getirilemedi');
        return null;
      }
    } catch (error) {
      console.error('Günlük istatistikler getirme hatası:', error);
      setError((error as any).response?.data?.message || 'Günlük istatistikler getirilemedi');
      return null;
    }
  };

  // Fetch weekly statistics
  const fetchWeeklyStats = async (startDate: string, locationId?: string) => {
    try {
      setLoading(true);
      clearError();

      const url = `/reports/weekly?startDate=${startDate}${locationId ? `&locationId=${locationId}` : ''}`;
      const response = await api.get(url);

      if (response.data.success) {
        dispatch({ type: 'SET_WEEKLY_STATS', payload: response.data.data });
        return response.data.data;
      } else {
        setError(response.data.message || 'Haftalık istatistikler getirilemedi');
        return null;
      }
    } catch (error) {
      console.error('Fetch weekly stats error:', error);
      setError('Haftalık istatistikler getirilirken hata oluştu');
      return null;
    }
  };

  // Fetch monthly statistics
  const fetchMonthlyStats = async (year: number, month: number, locationId?: string) => {
    try {
      setLoading(true);
      clearError();

      const url = `/reports/monthly?year=${year}&month=${month}${locationId ? `&locationId=${locationId}` : ''}`;
      const response = await api.get(url);

      if (response.data.success) {
        dispatch({ type: 'SET_MONTHLY_STATS', payload: response.data.data });
        return response.data.data;
      } else {
        setError(response.data.message || 'Aylık istatistikler getirilemedi');
        return null;
      }
    } catch (error) {
      console.error('Fetch monthly stats error:', error);
      setError('Aylık istatistikler getirilirken hata oluştu');
      return null;
    }
  };

  // Fetch advanced real-time statistics
  const fetchAdvancedRealtimeStats = async (locationId?: string) => {
    try {
      setLoading(true);
      clearError();

      const url = `/reports/advanced-realtime${locationId ? `?locationId=${locationId}` : ''}`;
      const response = await api.get(url);

      if (response.data.success) {
        dispatch({ type: 'SET_ADVANCED_REALTIME_STATS', payload: response.data.data });
        return response.data.data;
      } else {
        setError(response.data.message || 'Gelişmiş gerçek zamanlı istatistikler getirilemedi');
        return null;
      }
    } catch (error) {
      console.error('Fetch advanced realtime stats error:', error);
      setError('Gelişmiş gerçek zamanlı istatistikler getirilirken hata oluştu');
      return null;
    }
  };

  // Fetch agent performance
  const fetchAgentPerformance = async (agentId: string, startDate: string, endDate: string) => {
    try {
      setLoading(true);
      clearError();

      const response = await api.get(`/reports/agent-performance/${agentId}?startDate=${startDate}&endDate=${endDate}`);

      if (response.data.success) {
        dispatch({ type: 'SET_AGENT_PERFORMANCE', payload: response.data.data });
        return response.data.data;
      } else {
        setError(response.data.message || 'Agent performansı getirilemedi');
        return null;
      }
    } catch (error) {
      console.error('Fetch agent performance error:', error);
      setError('Agent performansı getirilirken hata oluştu');
      return null;
    }
  };

  // Fetch all agents performance
  const fetchAllAgentsPerformance = async (startDate: string, endDate: string, locationId?: string) => {
    try {
      setLoading(true);
      clearError();

      const url = `/reports/all-agents-performance?startDate=${startDate}&endDate=${endDate}${locationId ? `&locationId=${locationId}` : ''}`;
      const response = await api.get(url);

      if (response.data.success) {
        dispatch({ type: 'SET_ALL_AGENTS_PERFORMANCE', payload: response.data.data });
        return response.data.data;
      } else {
        setError(response.data.message || 'Tüm agent performansları getirilemedi');
        return null;
      }
    } catch (error) {
      console.error('Fetch all agents performance error:', error);
      setError('Tüm agent performansları getirilirken hata oluştu');
      return null;
    }
  };

  // Fetch queue performance details
  const fetchQueuePerformanceDetails = async (queueId: string, startDate: string, endDate: string) => {
    try {
      setLoading(true);
      clearError();

      const response = await api.get(`/reports/queue-performance/${queueId}?startDate=${startDate}&endDate=${endDate}`);

      if (response.data.success) {
        dispatch({ type: 'SET_QUEUE_PERFORMANCE_DETAILS', payload: response.data.data });
        return response.data.data;
      } else {
        setError(response.data.message || 'Kuyruk performans detayları getirilemedi');
        return null;
      }
    } catch (error) {
      console.error('Fetch queue performance details error:', error);
      setError('Kuyruk performans detayları getirilirken hata oluştu');
      return null;
    }
  };

  // Fetch daily breakdown
  const fetchDailyBreakdown = async (startDate: string, endDate: string, locationId?: string) => {
    try {
      setLoading(true);
      clearError();

      const url = `/reports/daily-breakdown?startDate=${startDate}&endDate=${endDate}${locationId ? `&locationId=${locationId}` : ''}`;
      const response = await api.get(url);

      if (response.data.success) {
        dispatch({ type: 'SET_DAILY_BREAKDOWN', payload: response.data.data });
        return response.data.data;
      } else {
        setError(response.data.message || 'Günlük dağılım getirilemedi');
        return null;
      }
    } catch (error) {
      console.error('Fetch daily breakdown error:', error);
      setError('Günlük dağılım getirilirken hata oluştu');
      return null;
    }
  };

  // Fetch weekly breakdown
  const fetchWeeklyBreakdown = async (startDate: string, endDate: string, locationId?: string) => {
    try {
      setLoading(true);
      clearError();

      const url = `/reports/weekly-breakdown?startDate=${startDate}&endDate=${endDate}${locationId ? `&locationId=${locationId}` : ''}`;
      const response = await api.get(url);

      if (response.data.success) {
        dispatch({ type: 'SET_WEEKLY_BREAKDOWN', payload: response.data.data });
        return response.data.data;
      } else {
        setError(response.data.message || 'Haftalık dağılım getirilemedi');
        return null;
      }
    } catch (error) {
      console.error('Fetch weekly breakdown error:', error);
      setError('Haftalık dağılım getirilirken hata oluştu');
      return null;
    }
  };

  // Fetch period comparison
  const fetchPeriodComparison = async (
    period1Start: string, 
    period1End: string, 
    period2Start: string, 
    period2End: string, 
    locationId?: string
  ) => {
    try {
      setLoading(true);
      clearError();

      const url = `/reports/period-comparison?period1Start=${period1Start}&period1End=${period1End}&period2Start=${period2Start}&period2End=${period2End}${locationId ? `&locationId=${locationId}` : ''}`;
      const response = await api.get(url);

      if (response.data.success) {
        dispatch({ type: 'SET_PERIOD_COMPARISON', payload: response.data.data });
        return response.data.data;
      } else {
        setError(response.data.message || 'Dönem karşılaştırması getirilemedi');
        return null;
      }
    } catch (error) {
      console.error('Fetch period comparison error:', error);
      setError('Dönem karşılaştırması getirilirken hata oluştu');
      return null;
    }
  };

  // Export to CSV
  const exportToCSV = async (data: any, filename: string) => {
    try {
      setLoading(true);
      clearError();

      const response = await api.post(`/reports/export/csv`, {
        data,
        filename
      });

      if (response.data.success) {
        return response.data.data;
      } else {
        setError(response.data.message || 'CSV export başarısız');
        return null;
      }
    } catch (error) {
      console.error('CSV export error:', error);
      setError('CSV export sırasında hata oluştu');
      return null;
    }
  };

  // Export to PDF
  const exportToPDF = async (data: any, filename: string) => {
    try {
      setLoading(true);
      clearError();

      const response = await api.post(`/reports/export/pdf`, {
        data,
        filename
      });

      if (response.data.success) {
        return response.data.data;
      } else {
        setError(response.data.message || 'PDF export başarısız');
        return null;
      }
    } catch (error) {
      console.error('PDF export error:', error);
      setError('PDF export sırasında hata oluştu');
      return null;
    }
  };

  // Fetch real-time statistics
  const fetchRealtimeStats = async () => {
    try {
      setLoading(true);
      clearError();

      const response = await api.get(`/reports/realtime`);

      if (response.data.success) {
        dispatch({ type: 'SET_REALTIME_STATS', payload: response.data.data });
        return response.data.data;
      } else {
        setError(response.data.message || 'Gerçek zamanlı istatistikler getirilemedi');
        return null;
      }
    } catch (error) {
      console.error('Gerçek zamanlı istatistikler getirme hatası:', error);
      setError((error as any).response?.data?.message || 'Gerçek zamanlı istatistikler getirilemedi');
      return null;
    }
  };

  // Fetch active agents
  const fetchActiveAgents = async (locationId?: string) => {
    try {
      setLoading(true);
      clearError();

      // Yeni agent-stats API'sini kullan (günlük istatistiklerle)
      const response = await api.get('/agent-stats/all/today');

      if (response.data.success) {
        // Eski format'a uyumlu hale getir
        const agentsData = response.data.data.map((stat: any) => ({
          _id: stat.agentId,
          name: stat.agentName,
          extension: stat.agentExtension,
          totalCalls: stat.totalCalls,
          answeredCalls: stat.answeredCalls,
          incomingCalls: stat.incomingCalls,
          outgoingCalls: stat.outgoingCalls,
          missedCalls: stat.missedCalls,
          totalDuration: stat.totalDuration,
          avgCallDuration: stat.avgCallDuration,
          successRate: stat.totalCalls > 0 ? Math.round((stat.answeredCalls / stat.totalCalls) * 100) : 0
        }));
        
        dispatch({ type: 'SET_ACTIVE_AGENTS', payload: agentsData });
        return agentsData;
      } else {
        setError(response.data.message || 'Aktif temsilciler getirilemedi');
        return [];
      }
    } catch (error) {
      console.error('Aktif temsilciler getirme hatası:', error);
      setError((error as any).response?.data?.message || 'Aktif temsilciler getirilemedi');
      return [];
    }
  };

  // Fetch waiting calls
  const fetchWaitingCalls = async (locationId?: string) => {
    try {
      setLoading(true);
      clearError();

      const params = locationId ? `?locationId=${locationId}` : '';
      const response = await api.get(`/reports/waiting-calls${params}`);

      if (response.data.success) {
        dispatch({ type: 'SET_WAITING_CALLS', payload: response.data.data });
        return response.data.data;
      } else {
        setError(response.data.message || 'Bekleyen çağrılar getirilemedi');
        return [];
      }
    } catch (error) {
      console.error('Bekleyen çağrılar getirme hatası:', error);
      setError((error as any).response?.data?.message || 'Bekleyen çağrılar getirilemedi');
      return [];
    }
  };

  // Fetch queue performance
  const fetchQueuePerformance = async (locationId?: string) => {
    try {
      setLoading(true);
      clearError();

      const params = locationId ? `?locationId=${locationId}` : '';
      const response = await api.get(`/reports/queue-performance${params}`);

      if (response.data.success) {
        dispatch({ type: 'SET_QUEUE_PERFORMANCE', payload: response.data.data });
        return response.data.data;
      } else {
        setError(response.data.message || 'Kuyruk performansı getirilemedi');
        return [];
      }
    } catch (error) {
      console.error('Kuyruk performansı getirme hatası:', error);
      setError((error as any).response?.data?.message || 'Kuyruk performansı getirilemedi');
      return [];
    }
  };

  // Fetch hourly distribution
  const fetchHourlyDistribution = async (date = new Date().toISOString().split('T')[0]) => {
    try {
      setLoading(true);
      clearError();

      const response = await api.get(`/reports/hourly-distribution?date=${date}`);

      if (response.data.success) {
        dispatch({ type: 'SET_HOURLY_DISTRIBUTION', payload: response.data.data });
        return response.data.data;
      } else {
        setError(response.data.message || 'Saatlik dağılım getirilemedi');
        return [];
      }
    } catch (error) {
      console.error('Saatlik dağılım getirme hatası:', error);
      setError((error as any).response?.data?.message || 'Saatlik dağılım getirilemedi');
      return [];
    }
  };

  // Fetch all reports
  const fetchAllReports = async (locationId = null, date = new Date().toISOString().split('T')[0]) => {
    try {
      setLoading(true);
      clearError();

      const promises = [
        fetchDailyStats(date),
        fetchRealtimeStats(),
        fetchActiveAgents(locationId || undefined),
        fetchWaitingCalls(locationId || undefined),
        fetchQueuePerformance(locationId || undefined),
        fetchHourlyDistribution(date)
      ];

      await Promise.all(promises);
      setLoading(false);
    } catch (error) {
      console.error('Tüm raporlar getirme hatası:', error);
      setError('Raporlar getirilemedi');
      setLoading(false);
    }
  };

  // Refresh reports
  const refreshReports = async (locationId = null) => {
    await fetchAllReports(locationId);
  };

  // Context value
  const value = {
    ...state,
    fetchDailyStats,
    fetchWeeklyStats,
    fetchMonthlyStats,
    fetchRealtimeStats,
    fetchAdvancedRealtimeStats,
    fetchActiveAgents,
    fetchWaitingCalls,
    fetchQueuePerformance,
    fetchHourlyDistribution,
    fetchAgentPerformance,
    fetchAllAgentsPerformance,
    fetchQueuePerformanceDetails,
    fetchDailyBreakdown,
    fetchWeeklyBreakdown,
    fetchPeriodComparison,
    exportToCSV,
    exportToPDF,
    fetchAllReports,
    refreshReports,
    setLoading,
    setError,
    clearError
  };

  return (
    <ReportsContext.Provider value={value}>
      {children}
    </ReportsContext.Provider>
  );
};

// Reports Hook
export const useReports = (): ReportsContextType => {
  const context = useContext(ReportsContext);
  if (!context) {
    throw new Error('useReports must be used within a ReportsProvider');
  }
  return context;
};

export default ReportsContext;
